import { ChevronLeft, ChevronRight } from '@untitledui/icons'
import { useCallback, useEffect, useRef, useState } from 'react'
import { twJoin } from 'tailwind-merge'

import type { ReactNode } from 'react'

interface HorizontalScrollerProps {
  children: ReactNode
  className?: string
  scrollAmount?: number
  testId?: string
}

export function HorizontalScroller({ children, className = '', scrollAmount = 200, testId }: HorizontalScrollerProps) {
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const [showLeftArrow, setShowLeftArrow] = useState(false)
  const [showRightArrow, setShowRightArrow] = useState(false)

  const updateArrowVisibility = useCallback(() => {
    const container = scrollContainerRef.current
    if (!container) return

    const { scrollLeft, scrollWidth, clientWidth } = container

    // Show left arrow if we've scrolled right (with small threshold)
    setShowLeftArrow(scrollLeft > 1)

    // Show right arrow if there's more content to scroll
    // Adding a threshold to account for rounding errors
    const maxScrollLeft = scrollWidth - clientWidth
    setShowRightArrow(scrollLeft < maxScrollLeft - 1)
  }, [])

  useEffect(() => {
    const container = scrollContainerRef.current
    if (!container) return

    // Initial check
    updateArrowVisibility()

    // Update on scroll
    container.addEventListener('scroll', updateArrowVisibility)

    // Update on resize (content or window size changes)
    const resizeObserver = new ResizeObserver(updateArrowVisibility)
    resizeObserver.observe(container)

    // Also observe mutations to the container
    const mutationObserver = new MutationObserver(updateArrowVisibility)
    mutationObserver.observe(container, {
      childList: true,
      subtree: true,
      attributes: true,
    })

    return () => {
      container.removeEventListener('scroll', updateArrowVisibility)
      resizeObserver.disconnect()
      mutationObserver.disconnect()
    }
  }, [updateArrowVisibility])

  const scrollLeft = () => {
    const container = scrollContainerRef.current
    if (!container) return

    container.scrollBy({
      left: -scrollAmount,
      behavior: 'smooth',
    })
  }

  const scrollRight = () => {
    const container = scrollContainerRef.current
    if (!container) return

    container.scrollBy({
      left: scrollAmount,
      behavior: 'smooth',
    })
  }

  return (
    <div className={twJoin('relative flex items-center', className)} data-testid={testId}>
      {/* Left gradient overlay */}
      {showLeftArrow && (
        <div className="pointer-events-none absolute left-0 z-10 h-full w-20 bg-linear-to-r from-white to-transparent" />
      )}

      {/* Left arrow button */}
      {showLeftArrow && (
        <div className="absolute left-0 z-20 flex h-full items-center">
          <button onClick={scrollLeft} aria-label="Scroll left" className="cursor-pointer">
            <ChevronLeft size={24} />
          </button>
        </div>
      )}

      {/* Scrollable content */}
      <div
        ref={scrollContainerRef}
        className="scrollbar-hide flex-1 overflow-x-auto overflow-y-hidden"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        <div className="inline-flex items-center">
          {children}
        </div>
      </div>

      {/* Right gradient overlay */}
      {showRightArrow && (
        <div className="pointer-events-none absolute right-0 z-10 h-full w-20 bg-linear-to-l from-white to-transparent" />
      )}

      {/* Right arrow button */}
      {showRightArrow && (
        <div className="absolute right-0 z-20 flex h-full items-center">
          <button onClick={scrollRight} aria-label="Scroll right" className="cursor-pointer">
            <ChevronRight size={24} />
          </button>
        </div>
      )}
    </div>
  )
}

